/**
 * Unified loading state. Delegates to blocks/loading (PanelLoading, PageLoading)
 * and uses semantic tokens only. Same API for backward compatibility.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import {
  PanelLoading,
  PageLoading,
  InlineSpinner,
} from '@/components/blocks/loading';

interface LoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'spinner' | 'skeleton' | 'overlay';
  fullScreen?: boolean;
  className?: string;
}

/**
 * LoadingState - Centered spinner, skeleton, or overlay.
 * Uses unified loading components from blocks/loading (semantic tokens).
 */
const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  variant = 'spinner',
  fullScreen = false,
  className = '',
}) => {
  if (variant === 'skeleton') {
    return (
      <div className={cn('animate-pulse space-y-4', className)} role="status" aria-label="Loading">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded" />
        <div className="h-4 bg-muted rounded w-5/6" />
      </div>
    );
  }

  if (variant === 'overlay' || fullScreen) {
    return (
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm',
          className
        )}
        role="status"
        aria-label="Loading"
      >
        <div className="rounded-xl border border-border bg-card p-8 shadow-xl">
          <PanelLoading message={message} />
        </div>
      </div>
    );
  }

  if (size === 'sm') {
    return (
      <div className={cn('flex items-center justify-center gap-2 py-4', className)} role="status" aria-label="Loading">
        <InlineSpinner size="sm" />
        {message && <span className="text-sm text-muted-foreground">{message}</span>}
      </div>
    );
  }

  if (size === 'lg') {
    return <PageLoading message={message} className={className} />;
  }

  return <PanelLoading message={message} className={className} />;
};

export default LoadingState;
